import React, { useMemo, useState } from 'react';
import { buildBins, posOf, conclusion, niceTicks } from './histogramBins';
import { num, pct } from './format';

const money = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`;
const CATS = {
  below: { label: 'Abaixo do limite', bar: 'bg-slate-400 dark:bg-slate-500', dot: 'bg-slate-400 dark:bg-slate-500' },
  near: { label: 'Quase lá', bar: 'bg-amber-500', dot: 'bg-amber-500' },
  above: { label: 'Acima do limite', bar: 'bg-primary', dot: 'bg-primary' },
  all: { label: 'Pedidos', bar: 'bg-slate-400 dark:bg-slate-500', dot: 'bg-slate-400 dark:bg-slate-500' },
};
const rangeLabel = (b) => (b.to == null ? `${money(b.from)} ou mais` : `${money(b.from)} a ${money(b.to)}`);

const Legend = ({ bins }) => {
  const keys = [...new Set(bins.map((b) => b.cat))].sort((a, b) => ['below', 'near', 'above', 'all'].indexOf(a) - ['below', 'near', 'above', 'all'].indexOf(b));
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="histogram-legend">
      {keys.map((k) => (
        <span key={k} className="flex items-center gap-1.5" data-testid={`histogram-legend-${k}`}>
          <span className={`h-2.5 w-2.5 rounded-sm ${CATS[k].dot}`} />
          {CATS[k].label} · {num(bins.filter((b) => b.cat === k).reduce((s, b) => s + b.count, 0))} pedidos
        </span>
      ))}
    </div>
  );
};

const Conclusion = ({ c }) => {
  if (!c) return null;
  const p = c.pct == null ? null : Math.round(Math.abs(c.pct));
  const diff = c.pct == null ? 'enquanto a faixa logo abaixo não tem pedidos'
    : p === 0 ? 'a mesma quantidade da faixa logo abaixo'
      : <><strong>{p}% {c.pct > 0 ? 'a mais' : 'a menos'}</strong> que na faixa logo abaixo</>;
  const below = c.pct == null ? '' : ` (${rangeLabel(c.down)}, ${num(c.down.count)} pedidos)`;
  const end = {
    more: 'Esse salto indica que o frete grátis está incentivando os clientes a completar o carrinho.',
    flat: 'Não há concentração de pedidos logo acima do limite: o frete grátis pode não estar incentivando o aumento do carrinho.',
    less: 'Há menos pedidos logo acima do limite: o limite pode estar alto demais para o ticket da loja.',
  }[c.kind];
  return (
    <div className="space-y-1" data-testid="histogram-conclusion" data-kind={c.kind}>
      <p className="text-sm">
        Na faixa <strong>logo acima do limite ({rangeLabel(c.up)})</strong> há <strong>{num(c.up.count)} pedidos</strong>, {diff}{below}. {end}
      </p>
      <p className="text-xs text-muted-foreground" data-testid="histogram-conclusion-total">
        No total, <strong>{Math.round(c.belowPct)}% dos pedidos</strong> ficam abaixo do limite e <strong>{100 - Math.round(c.belowPct)}%</strong> acima.
      </p>
    </div>
  );
};

// The chip shifts by its own width in proportion to the position, so it never leaves the plot.
const Marker = ({ left, label, tone, top, testId }) => (
  <div className="absolute inset-0 pointer-events-none z-10" data-testid={testId}>
    <div className={`absolute bottom-0 border-l-2 border-dashed ${tone.line}`} style={{ left: `${left * 100}%`, top }} />
    <span className={`absolute whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium shadow-sm ${tone.chip}`}
      style={{ top, left: `${left * 100}%`, transform: `translateX(-${left * 100}%)` }}>{label}</span>
  </div>
);
const LIMIT_TONE = { line: 'border-rose-500', chip: 'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-800' };
const SIM_TONE = { line: 'border-sky-500', chip: 'bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950 dark:text-sky-200 dark:border-sky-800' };

const Tip = ({ b, i, n, total }) => (
  <div className={`absolute top-16 z-20 w-48 rounded-md border bg-popover p-2 text-xs text-popover-foreground shadow-md ${i < n / 2 ? '' : '-translate-x-full'}`}
    style={{ left: `${((i < n / 2 ? i : i + 1) / n) * 100}%` }} data-testid="histogram-tooltip">
    <p className="font-medium">{rangeLabel(b)}</p>
    <p className="flex items-center gap-1.5 text-muted-foreground"><span className={`h-2 w-2 rounded-sm ${CATS[b.cat].dot}`} />{CATS[b.cat].label}</p>
    <p>{num(b.count)} pedidos ({pct(total ? (b.count / total) * 100 : 0)} do total)</p>
    <p>{pct(b.count ? (b.gratis / b.count) * 100 : 0)} com frete grátis</p>
  </div>
);

const XLabels = ({ bins }) => {
  const n = bins.length;
  const every = (desk) => (i) => i % desk === 0;
  const desk = every(n > 10 ? 2 : 1);
  const mob = every(n > 8 ? 3 : 2);
  return (
    <div className="flex">
      {bins.map((b, i) => (
        <span key={i} className={`flex-1 min-w-0 text-center text-[10px] text-muted-foreground whitespace-nowrap ${mob(i) ? '' : 'max-sm:invisible'} ${desk(i) ? '' : 'sm:invisible'}`}>
          {money(b.from)}{b.to == null ? '+' : ''}
        </span>
      ))}
    </div>
  );
};

export const OrdersHistogram = ({ orders, limit, sim }) => {
  const { bins, step, limitIdx } = useMemo(() => buildBins(orders, limit), [orders, limit]);
  const [active, setActive] = useState(null);
  const total = orders.length;
  const ticks = niceTicks(Math.max(...bins.map((b) => b.count)));
  const top = ticks[ticks.length - 1];
  const n = bins.length;
  const s = Number(sim);
  const showSim = s > 0 && s !== limit;
  return (
    <div className="space-y-3" data-testid="free-shipping-histogram">
      <p className="text-sm text-muted-foreground">Pedidos por valor dos produtos (R$) · só pedidos com entrega</p>
      <Conclusion c={conclusion(bins, limitIdx)} />
      <Legend bins={bins} />
      <div className="flex gap-2">
        <div className="flex items-center"><span className="text-[11px] text-muted-foreground [writing-mode:vertical-rl] rotate-180">Pedidos</span></div>
        <div className="relative h-[260px] sm:h-[320px] w-8 shrink-0 text-[10px] text-muted-foreground">
          <div className="absolute inset-x-0 bottom-0 top-14">
            {ticks.map((t) => <span key={t} className="absolute right-0 translate-y-1/2" style={{ bottom: `${(t / top) * 100}%` }}>{num(t)}</span>)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative h-[260px] sm:h-[320px]" onMouseLeave={() => setActive(null)}>
            <div className="absolute inset-x-0 bottom-0 top-14 border-b border-l">
              {ticks.map((t) => <div key={t} className="absolute inset-x-0 border-t border-dashed border-border" style={{ bottom: `${(t / top) * 100}%` }} />)}
            </div>
            <div className="absolute inset-x-0 bottom-0 top-14 flex items-end">
              {bins.map((b, i) => (
                <button type="button" key={i} className="flex-1 h-full flex items-end px-[2px] focus:outline-none" onMouseEnter={() => setActive(i)} onClick={() => setActive(active === i ? null : i)}
                  aria-label={`${rangeLabel(b)}: ${b.count} pedidos, ${CATS[b.cat].label}`} data-testid={`histogram-bar-${i}`} data-cat={b.cat}>
                  <span className={`w-full rounded-t-sm transition-opacity ${CATS[b.cat].bar} ${active === i ? 'opacity-80' : ''}`} style={{ height: `${(b.count / top) * 100}%` }} />
                </button>
              ))}
            </div>
            {limitIdx > 0 && <Marker left={limitIdx / n} label={`Frete grátis a partir de ${money(limit)}`} tone={LIMIT_TONE} top={2} testId="histogram-limit-line" />}
            {showSim && <Marker left={posOf(bins, step, s)} label={`Simulado: ${money(s)}`} tone={SIM_TONE} top={28} testId="histogram-sim-line" />}
            {active != null && <Tip b={bins[active]} i={active} n={n} total={total} />}
          </div>
          <div className="pt-1"><XLabels bins={bins} /></div>
          <p className="text-center text-[11px] text-muted-foreground mt-1">Valor dos produtos no pedido</p>
        </div>
      </div>
    </div>
  );
};
